import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { getStudentResultsMy } from '../../services/labResults.api';
import { formatDateTimeIST } from '../../utils/istDateTime';

function shortId(id) {
  if (id == null || typeof id !== 'string') return '—';
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

function getSubmissionLabel(row) {
  if (!row || typeof row !== 'object') return '';
  return String(
    row.testName ||
      row.labTitle ||
      row.labName ||
      row.title ||
      row.name ||
      row.labId ||
      ''
  ).trim();
}

function isExternalSubmission(row) {
  return getSubmissionLabel(row).toLowerCase().includes('external');
}

function getExecutionStatus(totalScore) {
  const score = Number(totalScore);
  if (!Number.isFinite(score)) return 'Not Executed';
  if (score > 75) return 'Executed';
  if (score >= 35) return 'Partially Executed';
  return 'Not Executed';
}

export default function StudentLabResultsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const list = await getStudentResultsMy();
        if (!cancelled) setRows(list);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.message || e?.message || 'Failed to load results');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        My lab results
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Scores reflect automated evaluation; open a row for full breakdown. Use your lab dashboard to submit
        or re-run evaluation.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Lab</TableCell>
                <TableCell align="right">Result</TableCell>
                <TableCell>Grade</TableCell>
                <TableCell>Verdict</TableCell>
                <TableCell>Evaluated</TableCell>
                <TableCell align="right">Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary">
                      No submissions yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.submissionId} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {shortId(r.labId)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {isExternalSubmission(r)
                        ? getExecutionStatus(r.totalScore)
                        : (r.totalScore ?? '—')}
                    </TableCell>
                    <TableCell>{r.grade ?? '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="body2" noWrap title={r.verdict || ''}>
                        {r.verdict ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {formatDateTimeIST(r.evaluatedAt, '—')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        component={RouterLink}
                        to={`/student/results/${r.submissionId}`}
                        size="small"
                        variant="outlined"
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
